import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Flex, IconButton, Image, Modal, ModalContent, ModalOverlay, Text, Tooltip,
} from '@chakra-ui/react';
import { FiChevronLeft, FiChevronRight, FiRotateCw, FiX, FiZoomIn, FiZoomOut } from 'react-icons/fi';
import { sanitizeStudentHtml } from 'utils/safeHtml';

/**
 * Respuesta del estudiante (HTML del editor) para el docente: saneada, con las imágenes ajustadas
 * al ancho y, al hacer clic en una, un visor en pantalla completa para leerla con calma
 * (ampliar, girar y pasar entre las imágenes de la respuesta).
 */
export default function StudentAnswer({ html, emptyHtml = '', ...boxProps }) {
  const ref = useRef(null);
  const [images, setImages] = useState([]);
  const [index, setIndex] = useState(null);
  const safe = useMemo(() => (html ? sanitizeStudentHtml(html) : emptyHtml), [html, emptyHtml]);

  // Las imágenes se vuelven botones accesibles con teclado.
  useEffect(() => {
    const imgs = ref.current ? [...ref.current.querySelectorAll('img')] : [];
    imgs.forEach((img, i) => {
      img.setAttribute('tabindex', '0');
      img.setAttribute('role', 'button');
      img.setAttribute('aria-label', img.alt ? `Ampliar imagen: ${img.alt}` : `Ampliar imagen ${i + 1}`);
    });
  }, [safe]);

  const open = useCallback((img) => {
    const all = [...ref.current.querySelectorAll('img')].filter((i) => i.getAttribute('src'));
    setImages(all.map((i) => i.getAttribute('src')));
    setIndex(Math.max(0, all.indexOf(img)));
  }, []);

  const onClick = (e) => {
    const img = e.target.closest?.('img');
    if (img && ref.current?.contains(img)) open(img);
  };
  const onKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && e.target.tagName === 'IMG') {
      e.preventDefault();
      open(e.target);
    }
  };

  return (
    <>
      <Box
        ref={ref}
        onClick={onClick}
        onKeyDown={onKeyDown}
        dangerouslySetInnerHTML={{ __html: safe }}
        sx={{
          img: {
            maxW: '100%', h: 'auto', my: 2, borderRadius: '6px', cursor: 'zoom-in',
            border: '1px solid', borderColor: 'gray.200', transition: 'box-shadow .15s',
          },
          'img:hover, img:focus-visible': { boxShadow: '0 0 0 3px rgba(87, 79, 235, .35)', outline: 'none' },
        }}
        {...boxProps}
      />
      <ImageViewer images={images} index={index} onChange={setIndex} onClose={() => setIndex(null)} />
    </>
  );
}

function ImageViewer({ images, index, onChange, onClose }) {
  const stageRef = useRef(null);
  const [zoom, setZoom] = useState(false);
  const [rotation, setRotation] = useState(0);
  const isOpen = index !== null && images.length > 0;
  const many = images.length > 1;

  useEffect(() => {
    setZoom(false);
    setRotation(0);
  }, [index]);

  const go = useCallback((step) => {
    if (!many) return;
    onChange((i) => (i + step + images.length) % images.length);
  }, [many, images.length, onChange]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, go]);

  const control = { variant: 'ghost', color: 'white', size: 'lg', borderRadius: '10px', _hover: { bg: 'whiteAlpha.200' } };
  const sideways = rotation % 180 !== 0;

  return (
    // El foco inicial va al área de la imagen (no a un botón) para que no aparezca su tooltip al abrir.
    <Modal isOpen={isOpen} onClose={onClose} size="full" motionPreset="none" initialFocusRef={stageRef}>
      <ModalOverlay bg="rgba(15, 23, 42, .96)" />
      <ModalContent bg="rgba(15, 23, 42, .96)" boxShadow="none" m={0} aria-label="Imagen de la respuesta">
        <Flex direction="column" h="100vh">
          <Flex align="center" justify="space-between" px={4} py={3} color="white">
            <Text fontSize="sm">{many ? `Imagen ${index + 1} de ${images.length}` : 'Imagen de la respuesta'}</Text>
            <Flex gap={1}>
              <Tooltip label={zoom ? 'Ajustar a la pantalla' : 'Ver a tamaño real'}>
                <IconButton aria-label={zoom ? 'Ajustar a la pantalla' : 'Ver a tamaño real'} icon={zoom ? <FiZoomOut /> : <FiZoomIn />} onClick={() => setZoom(!zoom)} {...control} />
              </Tooltip>
              <Tooltip label="Girar">
                <IconButton aria-label="Girar imagen" icon={<FiRotateCw />} onClick={() => setRotation((r) => (r + 90) % 360)} {...control} />
              </Tooltip>
              <Tooltip label="Cerrar (Esc)">
                <IconButton aria-label="Cerrar" icon={<FiX />} onClick={onClose} {...control} />
              </Tooltip>
            </Flex>
          </Flex>

          <Flex flex={1} minH={0} align="center" justify="center" position="relative" px={many ? 16 : 4} pb={4}>
            {many && (
              <IconButton aria-label="Imagen anterior" icon={<FiChevronLeft />} onClick={() => go(-1)} position="absolute" left={3} {...control} />
            )}
            <Box
              ref={stageRef} tabIndex={-1} outline="none"
              w="100%" h="100%" overflow={zoom ? 'auto' : 'hidden'} display="flex"
              alignItems={zoom ? 'flex-start' : 'center'} justifyContent={zoom ? 'flex-start' : 'center'}
              onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            >
              {isOpen && (
                <Image
                  src={images[index]}
                  alt={`Imagen ${index + 1} de la respuesta del estudiante`}
                  onClick={() => setZoom(!zoom)}
                  cursor={zoom ? 'zoom-out' : 'zoom-in'}
                  maxW={zoom ? 'none' : sideways ? 'calc(100vh - 140px)' : '100%'}
                  maxH={zoom ? 'none' : sideways ? '100%' : 'calc(100vh - 140px)'}
                  transform={`rotate(${rotation}deg)`}
                  transition="transform .2s"
                  borderRadius="6px"
                  bg="white"
                  m={zoom ? 'auto' : 0}
                />
              )}
            </Box>
            {many && (
              <IconButton aria-label="Imagen siguiente" icon={<FiChevronRight />} onClick={() => go(1)} position="absolute" right={3} {...control} />
            )}
          </Flex>
        </Flex>
      </ModalContent>
    </Modal>
  );
}
